import { LockOutlined as LockIcon, Search as SearchIcon } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Container,
    InputAdornment,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
    SelectChangeEvent,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useFormatter } from '../../context/FormatterContext';
import StyledPagination from '../common/StyledPagination';
import { useUserPlanStatus } from '../../hooks/useUserPlanStatus';
import { InsightData } from '../../services/insights.types';
import { AddonApiKey } from '../../services/payment.types';
import { getDisplayCoin } from '../../utils/displayFormatters';
import FilledTableSortLabel from '../common/FilledTableSortLabel';
import { formatCrypto } from '../../utils/formatters';
import PageLayout from '../common/PageLayout';
import { useInsightsService } from '../../services/insights.service';

type Order = 'asc' | 'desc';
type OrderBy = keyof InsightData;

interface HeadCell {
    id: OrderBy;
    label: string;
    numeric: boolean;
}

const createHeadCells = (t: (key: string) => string): readonly HeadCell[] => [
    { id: 'live_amount', label: t('insights.columns.liveAmount'), numeric: true },
    { id: 'remaining_amount', label: t('insights.columns.remainingAmount'), numeric: true },
    { id: 'currency', label: t('insights.columns.currency'), numeric: false },
    { id: 'cost_per_unit', label: t('insights.columns.costPerUnit'), numeric: true },
    { id: 'current_price', label: t('insights.columns.currentPrice'), numeric: true },
    { id: 'change', label: t('insights.columns.change'), numeric: true },
    { id: 'cost', label: t('insights.columns.cost'), numeric: true },
    { id: 'current_value', label: t('insights.columns.currentValue'), numeric: true },
    { id: 'unrealized_gain', label: t('insights.columns.unrealizedGain'), numeric: true },
    { id: 'realized_gain', label: t('insights.columns.realizedGain'), numeric: true },
];

const InsightsPage: React.FC = () => {
    const { t } = useTranslation();
    const headCells = useMemo(() => createHeadCells(t), [t]);
    const { formatCurrency, formatPercentage, formatNumber } = useFormatter();
    const insightsService = useInsightsService();
    const theme = useTheme();
    const { hasAddon, isLoadingUserPlans } = useUserPlanStatus();

    // Check if user has Portfolio Insights addon
    const hasPortfolioInsightsAddon = hasAddon(AddonApiKey.ADVANCED_ANALYTICS);

    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<OrderBy>('current_value');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    const isAllowed = hasPortfolioInsightsAddon && !isLoadingUserPlans;

    const { data: insightsData, isLoading } = useQuery({
        queryKey: ['insights-portfolio'],
        queryFn: () => insightsService.getGainsData(),
        enabled: isAllowed,
    });

    const hasInsights = !!insightsData && insightsData.length > 0;

    /*
    Page - stats_short_long.php
    TODO: Use something like main-app Php code for date(title)?

    if($period == "day") {
	$LANG_period1 = __("day");
	$LANG_period2 = __("days");
}
elseif($period == "week") {
	$LANG_period1 = __("week");
	$LANG_period2 = __("weeks");
}
else {
	$LANG_period1 = __("month");
	$LANG_period2 = __("months");
}

    if($period != "year") {
	if($i == 0) { $now_date = $now; if($date_full == date("d.m.Y", time())) { $LANG_date = __("today"); } else { $LANG_date = __("selected date"); } }
	elseif($i == 1) { $now_date = strtotime("+1 $period", $now); $LANG_date = "+1 $LANG_period1"; }
	elseif($i == 2) { $now_date = strtotime("+2 $period", $now); $LANG_date = "+2 $LANG_period2"; }
	elseif($i == 3) { $now_date = strtotime("+3 $period", $now); $LANG_date = "+3 $LANG_period2"; }
	elseif($i == 4) { $now_date = strtotime("+4 $period", $now); $LANG_date = "+4 $LANG_period2"; }
	elseif($i == 5) { $now_date = strtotime("+5 $period", $now); $LANG_date = "+5 $LANG_period2"; }
	elseif($i == 6) { $now_date = strtotime("+6 $period", $now); $LANG_date = "+6 $LANG_period2"; }
}
else { // period = year
	if($i == 0) { $now_date = $now; if($date_full == date("d.m.Y", time())) { $LANG_date = __("today"); } else { $LANG_date = __("selected date"); } }
	elseif($i == 1) { $now_date = strtotime("+2 month", $now); $LANG_date = "+2 $LANG_period2"; }
	elseif($i == 2) { $now_date = strtotime("+4 month", $now); $LANG_date = "+4 $LANG_period2"; }
	elseif($i == 3) { $now_date = strtotime("+6 month", $now); $LANG_date = "+6 $LANG_period2"; }
	elseif($i == 4) { $now_date = strtotime("+8 month", $now); $LANG_date = "+8 $LANG_period2"; }
	elseif($i == 5) { $now_date = strtotime("+10 month", $now); $LANG_date = "+10 $LANG_period2"; }
	elseif($i == 6) { $now_date = strtotime("+12 month", $now); $LANG_date = "+12 $LANG_period2"; }
}

$datum = date($F_DATE, $now_date);

    */
    const { data: shortLongData, isLoading: isShortLongLoading } = useQuery({
        queryKey: ['insights-short-long'],
        queryFn: () => insightsService.getShortLongPositions(null, null, null),
        enabled: hasInsights && isAllowed,
    });

    console.log(shortLongData)

    const { data: livePrice, isLoading: isLivePriceLoading } = useQuery({
        queryKey: ['insight-live-price', insightsData],
        queryFn: async () =>
            insightsService.getCoinBalanceForAccount(),
        enabled: hasInsights && isAllowed,
    });

    const handleRequestSort = (property: OrderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredInsights = useMemo(() => {
        if (!insightsData) return [];
        return insightsData.filter((insight) => insight.currency.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [insightsData, searchTerm]);

    // Apply sorting and pagination locally
    const sortedAndPaginatedInsights = useMemo(() => {
        // Add live price to each insight
        const insightsWithLivePrice = filteredInsights.map((insight) => ({
            ...insight,
            live_amount: livePrice?.find(price => price.coin === insight.currency)?.quantity,
        }));

        // Sort the insights with live price
        const sorted = [...insightsWithLivePrice].sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            return 0;
        });

        // Apply pagination
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return sorted.slice(start, end);
    }, [filteredInsights, orderBy, order, page, rowsPerPage, livePrice]);

    // Helper function to determine text color based on value
    const getValueColor = (value: number) => {
        if (value > 0) return 'success.main';
        if (value < 0) return 'error.main';
        return 'text.primary';
    };

    // Calculate sums for the footer
    const calculateSums = useMemo(() => {
        if (!filteredInsights || filteredInsights.length === 0) return null;

        const totalCost = filteredInsights.reduce((sum, insight) => sum + insight.cost, 0);
        const totalCurrentValue = filteredInsights.reduce((sum, insight) => sum + insight.current_value, 0);
        const averageChange = totalCost !== 0 ? (totalCurrentValue * 100) / totalCost - 100 : 0;

        return {
            averageChange,
            totalCost,
            totalCurrentValue,
            totalUnrealizedGain: filteredInsights.reduce((sum, insight) => sum + insight.unrealized_gain, 0),
            totalRealizedGain: filteredInsights.reduce((sum, insight) => sum + insight.realized_gain, 0),
        };
    }, [filteredInsights]);

    if (!isLoading && !isLoadingUserPlans && !hasPortfolioInsightsAddon) {
        return (
            // Show upgrade message if user doesn't have the addon
            <Box sx={{
                 display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                py: 6 
            }}>
                <LockIcon sx={{
                     fontSize: 48, 
                color: 'text.secondary',
                mb: 2 
                }} />
                <Typography variant="h6" sx={{mb: 1}}>
                    Portfolio Insights requires Portfolio Insights Addon
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{
                    mb: 3, 
                    textAlign: 'center' 
                }}>
                    Upgrade your plan to access detailed portfolio insights, including performance metrics, unrealized
                    gains, and live price tracking.
                </Typography>
                <Button variant="contained" color="primary" href="/plans">
                    Upgrade Now
                </Button>
            </Box>
        );
    }

    return (
        <PageLayout title={t('insights.title')}>
            <Box sx={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                p: 0
            }}>
                <Paper sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}>
                    <Box sx={{ 
                        p: 2,
                        borderBottom: '1px solid',
                        borderColor: theme.palette.divider
                    }}>
                        <TextField
                            fullWidth
                            placeholder={t('insights.search')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <TableContainer>
                        <Table sx={{ minWidth: 750 }}>
                            <TableHead>
                                <TableRow>
                                    {headCells.map((headCell) => (
                                        <TableCell
                                            key={headCell.id}
                                            align={headCell.numeric ? 'right' : 'left'}
                                            sortDirection={orderBy === headCell.id ? order : false}
                                        >
                                            <FilledTableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={orderBy === headCell.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(headCell.id)}
                                            >
                                                {headCell.label}
                                                {orderBy === headCell.id ? (
                                                    <Box component="span" sx={visuallyHidden}>
                                                        {order === 'desc' ? t('common.table.sort.descending') : t('common.table.sort.ascending')}
                                                    </Box>
                                                ) : null}
                                            </FilledTableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(isLoading || isLoadingUserPlans) ? (
                                    <TableRow>
                                        <TableCell colSpan={headCells.length} align="center">
                                            <Skeleton variant="rectangular" width="100%" height={40} sx={{ my: 1 }} />
                                        </TableCell>
                                    </TableRow>
                                ) : filteredInsights.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={headCells.length} align="center">
                                            {t('insights.noData')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedAndPaginatedInsights.map((insight) => (
                                        <TableRow
                                            key={insight.id}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell align="right">
                                            {isLivePriceLoading ? (
                                                    <Skeleton variant="rectangular" width="100%" height={40} sx={{ my: 1 }} />
                                                ) : insight.live_amount ? (
                                                    <Box component="div" sx={{ 
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500
                                                    }}>
                                                        {formatCrypto(insight.live_amount)}
                                                    </Box>
                                                ) : null}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatCrypto(insight.remaining_amount)}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ 
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <Avatar
                                                        src={`https://cointracking.info/img/coins/${insight.coin_image}`}
                                                        sx={{ 
                                                            width: 24,
                                                            height: 24
                                                        }}
                                                    />
                                                    {getDisplayCoin(insight.symbol, insight.currency)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">{formatCurrency(insight.cost_per_unit)}</TableCell>
                                            <TableCell align="right">
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-end'
                                                }}>
                                                    <Box component="div" sx={{ 
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500
                                                    }}>
                                                        {formatCurrency(insight.current_price)}
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        color={insight.trend_24h > 0 ? 'success.main' : 'error.main'}
                                                    >
                                                        {insight.trend_24h > 0 ? '+' : ''}
                                                        {formatPercentage(insight.trend_24h)}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box
                                                    component="div"
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        color: getValueColor(insight.change)
                                                    }}
                                                >
                                                    {formatPercentage(insight.change)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box component="div" sx={{ 
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500
                                                }}>
                                                    {formatCurrency(insight.cost)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box component="div" sx={{ 
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500
                                                }}>
                                                    {formatCurrency(insight.current_value)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box
                                                    component="div"
                                                    sx={{ 
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        color: getValueColor(insight.unrealized_gain)
                                                    }}
                                                >
                                                    {formatCurrency(insight.unrealized_gain)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box
                                                    component="div"
                                                    sx={{ 
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        color: getValueColor(insight.realized_gain)
                                                    }}
                                                >
                                                    {formatCurrency(insight.realized_gain)}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                                {/* Table Footer with Sums */}
                                {calculateSums && (
                                    <TableRow sx={{ 
                                        backgroundColor: theme.palette.action.hover,
                                        fontWeight: 'bold'
                                    }}>
                                        <TableCell colSpan={5} align="left">
                                            Sum:
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box
                                                component="div"
                                                sx={{ 
                                                    fontSize: '0.875rem',
                                                    fontWeight: 700,
                                                    color: getValueColor(calculateSums.averageChange)
                                                }}
                                            >
                                                {formatPercentage(calculateSums.averageChange)}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box component="div" sx={{ 
                                                fontSize: '0.875rem',
                                                fontWeight: 700
                                            }}>
                                                {formatCurrency(calculateSums.totalCost)}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box component="div" sx={{ 
                                                fontSize: '0.875rem',
                                                fontWeight: 700
                                            }}>
                                                {formatCurrency(calculateSums.totalCurrentValue)}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box
                                                component="div"
                                                sx={{ 
                                                    fontSize: '0.875rem',
                                                    fontWeight: 700,
                                                    color: getValueColor(calculateSums.totalUnrealizedGain)
                                                }}
                                            >
                                                {formatCurrency(calculateSums.totalUnrealizedGain)}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box
                                                component="div"
                                                sx={{ 
                                                    fontSize: '0.875rem',
                                                    fontWeight: 700,
                                                    color: getValueColor(calculateSums.totalRealizedGain)
                                                }}
                                            >
                                                {formatCurrency(calculateSums.totalRealizedGain)}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <StyledPagination
                        rowsPerPageOptions={[5, 10, 25]}
                        count={filteredInsights.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
        </PageLayout>
    );
};

export default InsightsPage;
